/**
 * `useSetupCollection(entity, opts)` → `[items, setItems, meta]`
 *
 * The Setup tabs keep their Phase-1 contract: hold the whole collection in
 * state and "save" it on every change. This hook implements that contract
 * against two backends:
 *
 * - **mocks** (`USE_MOCKS`): localStorage via `@mocks` — behaviour identical
 *   to before, including the cross-tab `subscribeKey` refresh.
 * - **server**: load once from webback, then on every change diff the
 *   collection against the last server snapshot and issue the per-entity
 *   POST / PUT / DELETE (debounced, one sync in flight, latest wins). On
 *   a server error: toast the server's `error` message **once**, remember
 *   the row it refused, and reload the server truth — quietly, keeping
 *   the operator's unsaved rows — so the UI never drifts from what was
 *   actually stored and never sends the same refusal twice (CU-869ffwj7a:
 *   before this, the reload re-armed the save and the 409 looped for ever).
 *
 * `meta = { loading, saving, error, reload, held, refused, stalled, source, remap }`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { loadKey, saveKey, subscribeKey } from 'Pages/DsRepricing/Setup/@mocks'
import useToast from '@hooks/useToast'
import * as adapters from './adapters'
import { setupApi } from './api'
import { adoptIds, createSyncQueue, syncCollection } from './collection'
import { USE_MOCKS } from './flag'
import { holdBack, kindOf, releaseSaved, releaseStalled } from './refused'
import { usePricelistCatalog } from './pricelists'

const SAVE_DEBOUNCE_MS = 600

const ENTITIES = {
	consistency: {
		mockKey: 'catConsistencyGroupsV5',
		api: setupApi.consistencyGroups,
		toServer: (g) => adapters.consistency.toServer(g, g.mode),
		fromServer: adapters.consistency.fromServer,
		fingerprint: (g) => adapters.consistency.fingerprint(g, g.mode),
	},
	priceLimits: {
		mockKey: 'priceLimits',
		api: setupApi.priceLimits,
		// The server refuses two limits with the same scope over the
		// same dates, and a clone is exactly that until it is edited.
		// Sending it produced an error toast on the very click that
		// made the row, so the copy stayed local and unexplained.
		//
		// A binding that has not picked its matrix yet is the same
		// situation: `matrix_id` is `''` while the select is still
		// empty, the adapter sends that as null, and the server then
		// sees an ordinary rule with no floor and says so. Hold it
		// until it names a matrix.
		// A protection without the two dates it protects is not a limit
		// yet; the server would 422 it (CU-869fe8jrk), so it is held
		// like a binding without a matrix.
		readyToSave: (r, all) => r.matrix_id !== ''
			&& !(r.protects_dates && !(r.date_from && r.date_to))
			&& !all.some(
				o => o !== r
				     && adapters.priceLimits.scopeKey(o) === adapters.priceLimits.scopeKey(r)),
		toServer: adapters.priceLimits.toServer,
		fromServer: adapters.priceLimits.fromServer,
		fingerprint: adapters.priceLimits.fingerprint,
	},
	priceMatrices: {
		mockKey: 'priceMatrices',
		api: setupApi.priceMatrices,
		// A matrix with no name cannot be saved and cannot be told
		// apart from the next one in the binding row, so it is held
		// locally until it has one — same reason price limits hold a
		// duplicate scope rather than toasting on the click that made it.
		readyToSave: (m) => !!(m.name || '').trim() && !!m.base_acriss,
		toServer: adapters.priceMatrices.toServer,
		fromServer: adapters.priceMatrices.fromServer,
		fingerprint: adapters.priceMatrices.fingerprint,
	},
	rateCards: {
		mockKey: 'rateCards_v2',
		api: setupApi.faces,
		// A face with no write pricelist is rejected by the server
		// (`face_needs_write`, PRD 01 §3) — and "Add face" starts with
		// none, so without this the row would be POSTed 600 ms later and
		// the operator would see an error for a form they had not filled
		// in yet. Hold it locally until it can succeed.
		readyToSave: (f) => (f.write_primary || []).some(Boolean)
			|| (f.write_derived || []).some(d => (d.pricelists || []).some(Boolean)),
		toServer: (f, maps) => adapters.rateCards.toServer(f, maps),
		fromServer: (row, maps) => adapters.rateCards.fromServer(row, maps),
		fingerprint: (f, maps) => adapters.rateCards.fingerprint(f, maps),
		needsCatalog: true,
	},
	tethers: {
		mockKey: 'tethering',
		api: setupApi.tethers,
		// Same reason: `tether_needs_child` rejects a childless tether.
		readyToSave: (t) => (t.children || []).some(Boolean),
		toServer: adapters.tethers.toServer,
		fromServer: adapters.tethers.fromServer,
		fingerprint: adapters.tethers.fingerprint,
	},
}

function describe(error) {
	if (error?.name === 'UnknownPricelist') return error.message
	if (error?.name === 'ApiError') {
		const d = error.details
		if (error.code === 'price_limit_duplicate' && d?.acriss?.length) {
			// The server names the ACRISS two matrices both price, and the
			// other matrix. That is the whole point of the message.
			return `${d.acriss.join(', ')} already covered in these dates by matrix "${d.existing_matrix || d.existing_matrix_id}" — remove it from one of the two`
		}
		if (error.code === 'tether_value_in_use' && d?.in_use) {
			return `${error.message}: ${Object.keys(d.in_use).join(', ')}`
		}
		if (error.code === 'validation_error' && d?.errors?.length) {
			return `${error.message}: ${d.errors.map(e => `${e.loc} ${e.msg}`).join('; ')}`
		}
		return error.message
	}
	return error?.message || 'Unexpected error'
}

export default function useSetupCollection(entity, { mockDefault = [], normalise = (x) => x } = {}) {
	const cfg = ENTITIES[entity]
	if (!cfg) throw new Error(`useSetupCollection: unknown entity "${entity}"`)
	const catalog = usePricelistCatalog()
	const maps = catalog.maps
	const catalogReady = !cfg.needsCatalog || !catalog.loading
	const { showToast } = useToast()

	// ------------------------------------------------------------------
	// Mock backend — unchanged Phase-1 behaviour.
	// ------------------------------------------------------------------
	const [items, setItems] = useState(() => {
		if (!USE_MOCKS) return []
		const stored = loadKey(cfg.mockKey, null)
		return (stored ?? mockDefault).map(normalise)
	})
	useEffect(() => {
		if (!USE_MOCKS) return undefined
		return subscribeKey(cfg.mockKey, (next) => setItems((next ?? []).map(normalise)))
	}, [cfg.mockKey])
	useEffect(() => {
		if (USE_MOCKS) saveKey(cfg.mockKey, items)
	}, [items])

	// ------------------------------------------------------------------
	// Server backend.
	// ------------------------------------------------------------------
	const [loading, setLoading] = useState(!USE_MOCKS)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [remap, setRemap] = useState(null)
	const serverRef = useRef([])        // last known server truth, client shape
	const sendableRef = useRef((rows) => ({ toSend: rows, held: new Set() }))
	// Rows the server turned down, `id → { fingerprint, message, status,
	// kind }`. The ref is the truth the queue reads synchronously; the
	// state is its published copy, so `held` and the chips recompute
	// when the map changes (TL: a ref alone would not re-render).
	const heldRef = useRef(new Map())
	const [heldState, setHeldState] = useState(() => new Map())
	const publishHeld = (map) => { heldRef.current = map; setHeldState(map) }
	// The array the last reload put in `items`, so the queue can tell "this
	// `next` is the reload's echo" from "the operator changed something".
	const lastReloadRef = useRef(null)
	const queue = useMemo(() => createSyncQueue(async (next) => {
		const ops = {
			create: async (item) => cfg.fromServer(await cfg.api.create(cfg.toServer(item, maps)), maps),
			update: async (id, item) => { await cfg.api.update(id, cfg.toServer(item, maps)) },
			remove: async (id) => { await cfg.api.remove(id) },
		}
		setSaving(true)
		// A `stalled` row goes again with the operator's next edit, and
		// only then: the cycle the error branch's own reload fires must
		// not resend it, or a 502 would loop like the 409 did.
		const released = releaseStalled(heldRef.current, next, lastReloadRef.current)
		if (released !== heldRef.current) publishHeld(released)
		// `next` is everything on screen; `toSend` holds back the rows
		// that cannot succeed yet — the new ones the operator has not
		// finished, and the ones the server refused as they stand. They
		// stay in the UI; a refused row the server already has travels
		// as the server's version, so the diff sees neither an update
		// nor a delete (plan §1.0: omitting it would DELETE it).
		const { toSend, held } = sendableRef.current(next)
		const r = await syncCollection(serverRef.current, toSend, ops, {
			fingerprint: (x) => cfg.fingerprint(x, maps),
		})
		const adopt = (rows) => adoptIds(rows, r.remap)
		if (r.ok) {
			serverRef.current = adopt(toSend)
			// Whatever travelled as the operator's own version is saved;
			// what travelled as the substitute stays held. By the ids of
			// before the remap, which is what the map is keyed on.
			const after = releaseSaved(heldRef.current, held)
			if (after !== heldRef.current) publishHeld(after)
			// Remap the list as it is NOW, not the snapshot that was
			// sent. `next` was captured before the await, and a sync is
			// long enough to drag three chips into a group: writing it
			// back threw those away and left the server row looking
			// like one to delete on the next pass (2026-09-28).
			if (r.remap.size) {
				setItems(cur => adopt(cur))
				// Published so a screen holding a selection can follow
				// it. A tab that keeps "which row am I editing" by id
				// loses its row the instant the first save returns the
				// server's id, and the editor goes blank mid-sentence.
				setRemap(r.remap)
			}
			setError(null)
		} else {
			setError(r.error)
			showToast.error(describe(r.error))
			// Remember the row that failed, as it was sent, so the next
			// cycle holds it back instead of sending it again. A 4xx is
			// the server's judgement on this row: `refused` until it
			// changes. Anything else (5xx, network, no status) says
			// nothing about the row: `stalled` until the next edit.
			if (r.failed?.item) {
				const map = new Map(heldRef.current)
				map.set(r.failed.item.id, {
					fingerprint: cfg.fingerprint(r.failed.item, maps),
					message: describe(r.error),
					status: r.error?.status ?? null,
					kind: kindOf(r.error),
				})
				publishHeld(map)
			}
			// Reload the server's truth, but keep the rows it has never
			// heard of. They are the operator's unsaved work, and the
			// refusal is usually about one of them — throwing them away
			// means the toast explains a problem with something that is
			// no longer on screen to fix (Curro, 2026-09-28). Adopted
			// first: the rows created before the failure already have
			// their server ids (`remap` comes back even on failure), and
			// keeping them under the client id duplicated them. Quiet:
			// the table must not flash to "Loading…" on every refusal.
			await reload({ keepUnsaved: adoptIds(next, r.remap), quiet: true })
		}
		setSaving(false)
	}), [maps])

	/**
	 * Rows worth sending, and the ids kept back. A row the server already
	 * knows always goes — dropping it here would read as a deletion; a
	 * refused one goes as the server's own version. A row that exists
	 * only on the client waits until it is complete enough to be
	 * accepted, so "Add" never fires an error for a half-filled form.
	 * "Complete enough" is judged on the whole list, not just the row:
	 * a clone is a perfect duplicate the instant it is made, and the
	 * server refuses duplicates, so it has to wait to be edited (Curro,
	 * 2026-09-28).
	 */
	const sendable = useCallback((rows) => holdBack(rows, {
		server: serverRef.current,
		held: heldRef.current,
		fingerprint: (x) => cfg.fingerprint(x, maps),
		readyToSave: cfg.readyToSave || null,
	}), [cfg, maps, heldState])
	sendableRef.current = sendable

	/**
	 * `quiet`: resync `serverRef` and `items` without touching `loading`
	 * — the error branch's reload, which must not replace the table with
	 * "Loading…" between the toast and the chip. An explicit reload (the
	 * tab's) also forgets every held row: the operator asked for the
	 * server's truth.
	 */
	const reload = useCallback(async ({ keepUnsaved = null, quiet = false } = {}) => {
		if (USE_MOCKS) return
		if (!quiet) setLoading(true)
		try {
			const rows = await cfg.api.list()
			const loaded = rows.map(row => cfg.fromServer(row, maps))
			serverRef.current = loaded
			const known = new Set(loaded.map(x => x.id))
			const pending = (keepUnsaved || []).filter(x => !known.has(x.id))
			const next = pending.length ? [...pending, ...loaded] : loaded
			lastReloadRef.current = next
			if (!quiet && heldRef.current.size) publishHeld(new Map())
			setItems(next)
			setError(null)
		} catch (e) {
			setError(e)
			showToast.error(describe(e))
		} finally {
			if (!quiet) setLoading(false)
		}
	}, [maps])

	useEffect(() => {
		if (USE_MOCKS || !catalogReady) return
		reload()
	}, [catalogReady])

	// Save on change: debounce, then diff against the server snapshot.
	const timer = useRef(null)
	//: What the debounce is holding. `null` once it has been handed to
	//: the queue, so the unmount flush below cannot send it twice.
	const waiting = useRef(null)
	const queueRef = useRef(queue)
	queueRef.current = queue

	useEffect(() => {
		if (USE_MOCKS || loading || !catalogReady) return undefined
		if (items === serverRef.current) return undefined
		waiting.current = items
		clearTimeout(timer.current)
		timer.current = setTimeout(() => {
			waiting.current = null
			queue.push(items)
		}, SAVE_DEBOUNCE_MS)
		return () => clearTimeout(timer.current)
	}, [items, loading, catalogReady])

	// Leaving the screen must not throw the save away.
	//
	// The effect above clears its timer on cleanup, and cleanup runs on
	// unmount as well as between renders — so a change made less than
	// 600 ms before navigating away was cancelled, silently, and the
	// work was gone. Reported on Category Consistency, where dragging a
	// handful of ACRISS and leaving lost the lot (Curro, 2026-09-28).
	//
	// The queue is not tied to this component: `push` starts a drain of
	// ordinary fetches that outlive the unmount, so flushing here
	// completes. Mount-only on purpose — this must run when the
	// component goes, not between renders.
	useEffect(() => () => {
		clearTimeout(timer.current)
		if (waiting.current) {
			queueRef.current.push(waiting.current)
			waiting.current = null
		}
	}, [])

	// Closing the tab is the one exit a flush cannot win: the requests
	// would be cut off mid-flight. Ask instead — the browser shows its
	// own wording, and only when something is actually unsaved.
	useEffect(() => {
		if (USE_MOCKS) return undefined
		const ask = (e) => {
			if (!waiting.current && !queueRef.current.busy) return
			e.preventDefault()
			e.returnValue = ''
		}
		window.addEventListener('beforeunload', ask)
		return () => window.removeEventListener('beforeunload', ask)
	}, [])

	// The rows being held back, so the screen can say so. Holding a row
	// silently is how the operator ends up believing a clone was saved
	// and losing it on the next reload (2026-09-28). Not derived from
	// what travels — a refused server row travels, as the substitute —
	// and computed whether or not the collection defines `readyToSave`.
	const held = useMemo(() => (USE_MOCKS ? new Set() : sendable(items).held), [items, sendable])
	//: `id → { message, status }` for the rows the server refused (red
	//: chip), `id → …` for the ones it did not answer about (amber).
	//: Only the rows still held — the map keeps an entry for a server
	//: row the reload reverted, so that repeating the same edit is held
	//: at once, but the chip must not say "your change is kept" over a
	//: row that shows the server's values again.
	const refused = useMemo(() => new Map([...heldState].filter(([id, e]) => e.kind === 'refused' && held.has(id))), [heldState, held])
	const stalled = useMemo(() => new Map([...heldState].filter(([id, e]) => e.kind === 'stalled' && held.has(id))), [heldState, held])

	const meta = { loading, saving, error, reload, held, refused, stalled,
	               source: USE_MOCKS ? 'mock' : 'server',
	               //: `Map(localId → serverId)` from the last save, for a
	               //: screen that holds a selection by id.
	               remap }
	return [items, setItems, meta]
}
